import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../../auth/hooks/useAuth";
import UpdateBanner from "../../../shared/components/UpdateBanner";
import { getProfile } from "../../auth/services/authService";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import i18n from "../../../shared/i18n/i18n";
import { useLang } from "../../../shared/i18n/useLang";
import { guardarPermisos, puede } from "../../../shared/utils/permisos";
import InstalarAppButton from "../../agua/components/InstalarAppButton";
import { listarRegistros } from "../services/seguridadService";
// La Garita no pasa por Layout: carga acá las variables de tema de Corvana.
import "../../../shared/styles/main.css";
import "../garita.css";

/**
 * Mientras se está en /garita, el <link rel="manifest"> apunta al de la app de
 * Seguridad: al instalarla, el celular la guarda como una app aparte
 * ("Seguridad", escudo naranja, abre en /garita). Misma técnica que useManifestAgua.
 */
function useManifestGarita() {
  useEffect(() => {
    const cambios = [];
    const set = (sel, attr, valor, crear) => {
      let el = document.querySelector(sel);
      let creado = false;
      if (!el && crear) {
        el = crear();
        document.head.appendChild(el);
        creado = true;
      }
      if (!el) return;
      cambios.push({ el, attr, antes: el.getAttribute(attr), creado });
      el.setAttribute(attr, valor);
    };
    set('link[rel="manifest"]', "href", "/manifest-garita.json");
    set('meta[name="theme-color"]', "content", "#f97316");
    set('link[rel="apple-touch-icon"]', "href", "/GARITA-ICON.png");
    set('meta[name="apple-mobile-web-app-title"]', "content", "Seguridad", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "apple-mobile-web-app-title");
      return m;
    });
    return () => cambios.forEach(({ el, attr, antes, creado }) => {
      if (creado) el.remove();
      else if (antes !== null) el.setAttribute(attr, antes);
    });
  }, []);
}

/**
 * Cascarón de la Garita: barra con el condominio y los botones de idioma, tema,
 * instalar y salir; el contenido de cada pantalla, y el menú de abajo. Comparte
 * con las pantallas (useOutletContext) cuántas personas hay adentro.
 */
export default function GaritaShell() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const L = useLang();
  const [adentro, setAdentro] = useState(0);

  useManifestGarita();

  const condominios = JSON.parse(
    localStorage.getItem("condominios_contador") || localStorage.getItem("condominios_admin") || "[]");
  const activoId = localStorage.getItem("active_condominio_id") || "";
  const activo = condominios.find((c) => String(c.id) === String(activoId));
  const nombreGuarda = [localStorage.getItem("first_name"), localStorage.getItem("last_name")]
    .filter(Boolean).join(" ");

  const recargarAdentro = useCallback(async () => {
    try {
      const lista = await listarRegistros({ adentro: 1 });
      setAdentro(lista.length);
    } catch { /* el contador no es crítico */ }
  }, []);

  useEffect(() => {
    recargarAdentro();
    // Permisos al día (el admin puede cambiarlos sin que el guarda cierre sesión).
    getProfile()
      .then((p) => {
        const permisos = p?.usuario_condominio?.permisos;
        if (Array.isArray(permisos)) guardarPermisos(permisos);
        if (p?.first_name) localStorage.setItem("first_name", p.first_name);
        if (p?.last_name) localStorage.setItem("last_name", p.last_name);
      })
      .catch(() => {});
    const t = setInterval(recargarAdentro, 30000);
    return () => clearInterval(t);
  }, [recargarAdentro]);

  function cambiarCondominio(e) {
    localStorage.setItem("active_condominio_id", e.target.value);
    window.location.reload();
  }

  function cambiarIdioma() {
    const nuevo = i18n.language === "es" ? "en" : "es";
    i18n.changeLanguage(nuevo);
    localStorage.setItem("lang", nuevo);
  }

  const TABS = [
    { to: "/garita",           icon: "fa-magnifying-glass", label: L("Buscar", "Search"), end: true },
    { to: "/garita/registrar", icon: "fa-circle-plus",      label: L("Registrar", "Register") },
    { to: "/garita/adentro",   icon: "fa-people-arrows",    label: L("Adentro", "Inside") },
  ];

  if (!puede("seguridad_garita")) {
    return (
      <div className="gar-app">
        <div className="gar-main">
          <div className="gar-aviso error" style={{ marginTop: 40 }}>
            <i className="fa-solid fa-lock" />
            <span>{L("Tu usuario no tiene acceso a Seguridad en este condominio. Pedile acceso al administrador.",
                     "Your user can't access Security in this condominium. Ask the administrator for access.")}</span>
          </div>
          <button type="button" className="gar-btn gar-btn-sec" style={{ marginTop: 14 }} onClick={logout}>
            {L("Cerrar sesión", "Log out")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gar-app">
      <header className="gar-top">
        <div className="gar-logo"><i className="fa-solid fa-shield-halved" /></div>
        <div className="gar-top-titulo">
          <strong>{L("Seguridad", "Security")}</strong>
          {condominios.length > 1 ? (
            <select value={activoId} onChange={cambiarCondominio} aria-label={L("Condominio", "Condominium")}>
              {condominios.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          ) : (
            <span>
              {activo?.nombre || localStorage.getItem("condominio_nombre") || L("Condominio", "Condominium")}
              {nombreGuarda ? ` · ${nombreGuarda}` : ""}
            </span>
          )}
        </div>
        <InstalarAppButton compacto ocultarSiInstalada />
        <button type="button" className="gar-icono-btn" onClick={cambiarIdioma}
          title={L("Switch to English", "Cambiar a español")} aria-label={L("Cambiar idioma", "Change language")}>
          {i18n.language === "es" ? "EN" : "ES"}
        </button>
        <button type="button" className="gar-icono-btn" onClick={toggleTheme}
          title={theme === "light" ? L("Tema oscuro", "Dark theme") : L("Tema claro", "Light theme")}
          aria-label={L("Cambiar tema", "Change theme")}>
          <i className={`fa-solid ${theme === "light" ? "fa-moon" : "fa-sun"}`} />
        </button>
        <button type="button" className="gar-icono-btn" onClick={logout}
          title={L("Cerrar sesión", "Log out")} aria-label={L("Cerrar sesión", "Log out")}>
          <i className="fa-solid fa-right-from-bracket" />
        </button>
      </header>

      <main className="gar-main">
        <Outlet context={{ adentro, recargarAdentro }} />
      </main>

      <nav className="gar-nav">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => (isActive ? "activo" : "")}>
            <i className={`fa-solid ${t.icon}`} />
            {t.label}
            {t.to === "/garita/adentro" && adentro > 0 && <span className="gar-badge">{adentro}</span>}
          </NavLink>
        ))}
      </nav>

      <UpdateBanner />
    </div>
  );
}
